// Create a channel / DM / group DM and seed its membership rows. Used by
// NewChannelModal and by the "DM" button on PersonCard.
import { getSDK } from '../sdk.js'
import { rowToChannel } from './chatUtils.js'

export const createChannel = async function ({ kind = 'channel', name = null, topic = null, isPrivate = false, memberEmails = [] }) {
  const root = this.state.root
  const me = root.currentUserEmail
  const sdk = getSDK()
  const allMembers = Array.from(new Set([me, ...memberEmails])).filter(Boolean)

  // For DMs, reuse an existing 1:1 channel between the same two emails if one
  // already exists — Slack-style. Group DMs (3+ members) reuse on exact set
  // match.
  if (kind === 'dm' || kind === 'group_dm') {
    const sortedKey = allMembers.slice().sort().join(',')
    const candidates = (root.channels || []).filter(c => c.kind === kind)
    for (const c of candidates) {
      const cm = (root.channelMembers || [])
        .filter(m => m.channel_id === c.id)
        .map(m => m.member_email)
        .sort()
        .join(',')
      if (cm === sortedKey) return c.id
    }
  }

  try {
    // migrated 2026-04-27 to SDK route workspaceProject.chat create
    // The wrapper's chat.createChannel may seed members server-side based on
    // the payload — we still post member rows below to make sure local state
    // reconciles correctly even if the wrapper is on an older deploy.
    const created = await sdk.execute('workspaceProject.chat', 'create', {
      payload: {
        kind,
        name,
        topic,
        is_private: isPrivate,
        created_by: me,
        members: allMembers
      }
    })
    const chRow = created?.data || created
    const channel = rowToChannel(chRow)

    const memberRows = allMembers.map(email => ({
      channel_id: channel.id,
      member_email: email,
      role: email === me ? 'owner' : 'member'
    }))
    // migrated 2026-04-27 to SDK route workspaceProject.chat.members create
    // If the server already inserted the rows (idempotent on (channel,email)),
    // these calls are no-ops; if not, they ensure membership is seeded.
    await Promise.all(
      memberRows.map(row =>
        sdk.execute('workspaceProject.chat.members', 'create', {
          channelId: channel.id,
          payload: row
        }).catch(err => {
          // Tolerate duplicate-row errors on the second insert path; surface
          // anything else to the outer catch.
          if (!/duplicate|already exists|conflict|409/i.test(err?.message || '')) throw err
        })
      )
    )

    // Guard against duplicates — realtime may push the same row.
    const existingChannels = root.channels || []
    const alreadyExists = existingChannels.some(c => c.id === channel.id)
    root.update({
      channels: alreadyExists ? existingChannels : [...existingChannels, channel],
      channelMembers: [...(root.channelMembers || []), ...memberRows.map(r => ({ ...r, last_read_at: new Date().toISOString() }))],
      activeChannelId: channel.id,
      activeThreadRootId: null,
      activeTab: 'conversation',
      showNewChannel: false,
      newChannelDraft: { name: '', topic: '', kind: 'channel', is_private: false, members: [] }
    })
    return channel.id
  } catch (e) {
    console.error('[createChannel]', e?.message || e)
    return null
  }
}
